import { inject, Injectable, input } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  brelaBillConfirmationFailed,
  brelaBillConfirmationRequest,
  brelaBillConfirmationSuccess,
  brelaVerificationFailed,
  brelaVerificationRequest,
  brelaVerificationSuccess,
  companyCurrentDirectorsFailed,
  companyCurrentDirectorsRequest,
  companyCurrentDirectorsSuccess,
  companyInformationFailed,
  companyInformationRequest,
  companyInformationSuccess,
  companyProfileFailed,
  companyProfileRequest,
  companyProfileSuccess,
  companyShareholdersFailed,
  companyShareholdersRequest,
  companyShareholdersSuccess,
  companySubmitFailed,
  companySubmitRequest,
  companySubmitSuccess,
  companyTaxInformationFailed,
  companyTaxInformationRequest,
  companyTaxInformationSuccess,
  FeeConfigByTypeActions,
  registrationProgressFailed,
  registrationProgressRequest,
  registrationProgressSuccess,
  tinVerificationFailed,
  tinVerificationRequest,
  tinVerificationSuccess,
  UpdateCompanyAddressDescriptionActions,
  UpdateContactDetailsActions,
} from './company-profile.action';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, exhaustMap, map, of, switchMap, tap } from 'rxjs';
import {
  BRELA_BILL_CONFIRMATION,
  GET_COMPANY_INFORMATION,
  GET_FEE_CONFIGURATION_BY_FEE_TYPE,
  GET_REGISTRATION_PROGRESS,
  SAVE_COMPANY_INFORMATION,
  SAVE_COMPANY_TAX_INFORMATION,
  SAVE_DIRECTOS_INFORMATION,
  SAVE_SHAREHOLDERS_INFORMATION,
  SUBMIT_COMPANY_REGISTRATION,
  UPDATE_COMPANY_ADDRESS_DESCRIPTION,
  UPDATE_CONTACT_DETAILS,
  VERIFY_BRELA_REGISTRATION,
  VERIFY_TIN_REGISTRATION,
} from '../graphql/company-profile.graphql';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { SaveCompanyInformationResponse } from '../responses/company-profile.response';
import { RegistrationProgressResponse } from '../responses/registration-response';
import { CompanyByUuidResponse } from '../responses/get-company-uuid';
import { SaveTaxInformationResponse } from '../responses/tax-information-reponse';
import { SaveShareholdersResponse } from '../responses/shareholders.response';
import { SaveCurrentDirectorsResponse } from '../responses/company-directos';
import { BrelaVerificationResponse } from '../responses/brela-verification.response';
import { TinVerificationResponse } from '../responses/tin.response';
import { SubmitCompanyRegistrationResponse } from '../responses/sumit-company.response';
import { FeeConfigByTypeResponse } from '../responses/fee-config-by-type.response';
import { BrelaBillConfirmationResponse } from '../responses/brela-bill-confirmation.response';
import { UpdateContactDetailsResponse } from '../responses/update-contact-details.response';
import { UpdateCompanyAddressDescriptionResponse } from '../responses/update-company-address.response';

@Injectable({
  providedIn: 'root',
})
export class CompanyProfileEffects {
  private actions$ = inject(Actions);
  private graphQLService = inject(GraphQlService);

  $saveCompanyInformations = createEffect(() =>
    this.actions$.pipe(
      ofType(companyProfileRequest),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<SaveCompanyInformationResponse>({
            mutation: SAVE_COMPANY_INFORMATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.saveCompanyInformation;

              if (!payload?.status) {
                return companyProfileFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return companyProfileSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                companyProfileFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $saveCompanyTaxInformations = createEffect(() =>
    this.actions$.pipe(
      ofType(companyTaxInformationRequest),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<SaveTaxInformationResponse>({
            mutation: SAVE_COMPANY_TAX_INFORMATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.saveTaxInformation;

              if (!payload?.status) {
                return companyTaxInformationFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return companyTaxInformationSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                companyTaxInformationFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $saveShareholders = createEffect(() =>
    this.actions$.pipe(
      ofType(companyShareholdersRequest),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<SaveShareholdersResponse>({
            mutation: SAVE_SHAREHOLDERS_INFORMATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { shareholders: request.shareholders, companyUuid: request.companyUuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.updateShareholders;

              if (!payload?.status) {
                return companyShareholdersFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return companyShareholdersSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                companyShareholdersFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $saveDirectors = createEffect(() =>
    this.actions$.pipe(
      ofType(companyCurrentDirectorsRequest),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<SaveCurrentDirectorsResponse>({
            mutation: SAVE_DIRECTOS_INFORMATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { directors: request.directors, companyUuid: request.companyUuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.updateDirectors;

              if (!payload?.status) {
                return companyCurrentDirectorsFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return companyCurrentDirectorsSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                companyCurrentDirectorsFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $submitRegistration = createEffect(() =>
    this.actions$.pipe(
      ofType(companySubmitRequest),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<SubmitCompanyRegistrationResponse>({
            mutation: SUBMIT_COMPANY_REGISTRATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { companyUuid: request.companyUuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.submitCompanyRegistration;

              if (!payload?.status) {
                return companySubmitFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return companySubmitSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                companySubmitFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getRegistrationProgress = createEffect(() =>
    this.actions$.pipe(
      ofType(registrationProgressRequest),
      exhaustMap(({ request }) =>
        this.graphQLService
          .query<RegistrationProgressResponse>({
            query: GET_REGISTRATION_PROGRESS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { companyUuid: request.companyUuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.getCompanyRegistrationProgress;
              if (!payload?.status) {
                return registrationProgressFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to fetch registration progress',
                    data: null,
                  },
                });
              }
              return registrationProgressSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                registrationProgressFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getCompanyInformations = createEffect(() =>
    this.actions$.pipe(
      ofType(companyInformationRequest),
      switchMap(({ request }) =>
        this.graphQLService
          .query<CompanyByUuidResponse>({
            query: GET_COMPANY_INFORMATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              includeStakeholders: request.includeStakeholders,
              brelaVerificationRequest: request.brelaVerificationRequest,
              attachmentsIncluded: request.attachmentsIncluded,
              contactDetailsIncluded: request.contactDetailsIncluded,
              includeRegistrationProgress: request.includeRegistrationProgress,
              includeOMC: request.includeOMC,
              includeTerminal: request.includeTerminal,
              includeSupplier: request.includeSupplier,
              includeNonBpsOperator: request.includeNonBpsOperator,
            },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.getMyCompany;
              if (!payload?.status) {
                return companyInformationFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to fetch registration progress',
                    data: payload,
                  },
                });
              }
              return companyInformationSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                companyInformationFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: err,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $brelaVerification = createEffect(() =>
    this.actions$.pipe(
      ofType(brelaVerificationRequest),
      exhaustMap(({ request }) =>
        this.graphQLService
          .query<BrelaVerificationResponse>({
            query: VERIFY_BRELA_REGISTRATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              brelaNumber: request.brelaNumber,
              confirmUsingDifferentNumber: request.differentBrela,
            },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.verifyBrelaRegistration;
              if (!payload?.status) {
                return brelaVerificationFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to fetch registration progress',
                    data: null,
                  },
                });
              }
              return brelaVerificationSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                brelaVerificationFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $tinVerification = createEffect(() =>
    this.actions$.pipe(
      ofType(tinVerificationRequest),
      exhaustMap(({ request }) =>
        this.graphQLService
          .query<TinVerificationResponse>({
            query: VERIFY_TIN_REGISTRATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.verifyTaxId;
              const tinData = payload?.data;
              const hasTaxpayerRecord = Boolean(tinData?.taxpayerName || tinData?.tin);

              const isNoRecord =
                !payload?.status ||
                !hasTaxpayerRecord ||
                payload?.code === 'NO_RECORD_FOUND';

              if (isNoRecord) {
                const isDeceptiveSuccessMessage = payload?.message?.toLowerCase().includes('success');
                const message =
                  payload?.message && !isDeceptiveSuccessMessage
                    ? payload.message
                    : 'No taxpayer record found for the provided TIN with TRA. Please verify your TIN and try again.';

                return tinVerificationFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message,
                    data: null,
                  },
                });
              }

              return tinVerificationSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                tinVerificationFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getFeeConfigByType = createEffect(() =>
    this.actions$.pipe(
      ofType(FeeConfigByTypeActions.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .query<FeeConfigByTypeResponse>({
            query: GET_FEE_CONFIGURATION_BY_FEE_TYPE,
            apolloNamespace: ApolloNamespace.billing,
            variables: { feeType: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.getFeeConfigurationByType;

              if (!payload) {
                return FeeConfigByTypeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to fetch fee configuration',
                    data: payload,
                  },
                });
              }
              return FeeConfigByTypeActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Fee configuration fetched successfully',
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FeeConfigByTypeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: err,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $brelaBillConfirmation = createEffect(() =>
    this.actions$.pipe(
      ofType(brelaBillConfirmationRequest),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<BrelaBillConfirmationResponse>({
            mutation: BRELA_BILL_CONFIRMATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { companyUuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.brelaBillConfirmation;

              if (!payload?.status) {
                return brelaBillConfirmationFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Brela bill confirmation failed',
                    data: null,
                  },
                });
              }
              return brelaBillConfirmationSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                brelaBillConfirmationFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updateContactDetails = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateContactDetailsActions.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<UpdateContactDetailsResponse>({
            mutation: UPDATE_CONTACT_DETAILS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              contactDetails: request.contactDetails,
              contactUuid: request.contactUuid ?? null,
            },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.updateContactDetails;

              if (!payload?.status) {
                return UpdateContactDetailsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to update contact details',
                    data: null,
                  },
                });
              }
              return UpdateContactDetailsActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                UpdateContactDetailsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updateCompanyAddressDescription = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateCompanyAddressDescriptionActions.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<UpdateCompanyAddressDescriptionResponse>({
            mutation: UPDATE_COMPANY_ADDRESS_DESCRIPTION,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              addressDescription: request.addressDescription,
            },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.updateCompanyAddressDescription;

              if (!payload?.status) {
                return UpdateCompanyAddressDescriptionActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to update address description',
                    data: null,
                  },
                });
              }
              return UpdateCompanyAddressDescriptionActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                UpdateCompanyAddressDescriptionActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
